import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { getFormAttachmentUrl } from '../utils/api';
import styles from './AttachmentLightbox.module.css';

// Full-screen viewer for form attachments.
//
// `attachments` is the full mixed list (images / videos / docs); we filter
// internally to image+video for the navigable set, since PDFs and other
// docs aren't meaningfully "viewable" inline. The caller passes the id
// the user clicked; we map it to the index inside the filtered list.
//
// Controls:
//   • X (top-right)         → close
//   • ◀ / ▶ side buttons    → prev / next (hidden at ends)
//   • Esc                   → close
//   • ← / →                 → prev / next
//   • Download button       → save current item
//   • Click outside the     → close
//     image/video itself
//
// Image zoom/pan (no effect on videos/docs):
//   • Mouse wheel (or trackpad) zooms in/out toward the cursor
//   • Two-finger pinch zooms toward the gesture's midpoint
//   • Drag (mouse or single finger) pans when zoomed in
//   • Double-click / double-tap toggles between 1x and 2.5x
//   • Navigating to a different image resets zoom to 1x
//   • Pan in two directions composes with pinch-zoom in the same gesture

export default function AttachmentLightbox({ attachments, submissionId, activeId, onClose }) {
  // Build the navigable list once per `attachments` change. Docs are
  // included only if the user clicked one (so it shows alone) — otherwise
  // we want photos/videos only in the carousel.
  const media = attachments.filter((a) =>
    a.mime_type?.startsWith('image/') || a.mime_type?.startsWith('video/')
  );

  // If the user clicked something that isn't in our media set (a doc), fall
  // back to showing it solo by surfacing it via a single-element list.
  const fallback = !media.find((a) => a.id === activeId)
    ? attachments.find((a) => a.id === activeId)
    : null;
  const list = fallback ? [fallback] : media;
  const startIdx = Math.max(0, list.findIndex((a) => a.id === activeId));

  const [idx, setIdx] = useState(startIdx);
  // Reset when the active id changes (e.g. user closed and re-opened on a
  // different item without navigating internally).
  useEffect(() => { setIdx(Math.max(0, list.findIndex((a) => a.id === activeId))); /* eslint-disable-next-line */ }, [activeId, attachments]);

  const current = list[idx];
  const hasPrev = idx > 0;
  const hasNext = idx < list.length - 1;

  function goPrev(e) { e?.stopPropagation(); if (hasPrev) setIdx(idx - 1); }
  function goNext(e) { e?.stopPropagation(); if (hasNext) setIdx(idx + 1); }

  // Keyboard handlers — Esc closes; arrow keys navigate. Pause video on
  // navigation so audio doesn't keep playing in the background.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape')      onClose();
      else if (e.key === 'ArrowLeft')  goPrev();
      else if (e.key === 'ArrowRight') goNext();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, list.length]);

  // Lock body scroll while the lightbox is open. Restore on unmount even if
  // unmounted via a re-render rather than onClose.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!current) return null;

  const isImage = current.mime_type?.startsWith('image/');
  const isVideo = current.mime_type?.startsWith('video/');
  const url     = getFormAttachmentUrl(submissionId, current.id);
  const dlUrl   = getFormAttachmentUrl(submissionId, current.id, { download: true });

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <button
        className={styles.close}
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        title="Close (Esc)"
        aria-label="Close"
      >
        <CloseIcon />
      </button>

      {hasPrev && (
        <button
          className={`${styles.nav} ${styles.navLeft}`}
          onClick={goPrev}
          title="Previous (←)"
          aria-label="Previous"
        >
          <ChevronLeft />
        </button>
      )}

      {hasNext && (
        <button
          className={`${styles.nav} ${styles.navRight}`}
          onClick={goNext}
          title="Next (→)"
          aria-label="Next"
        >
          <ChevronRight />
        </button>
      )}

      {/* Stage: clicks on the media itself shouldn't close (only clicks on
          the surrounding backdrop). Buttons stop propagation already. */}
      <div className={styles.stage} onClick={(e) => e.stopPropagation()}>
        {isImage && (
          <ZoomableImage
            // key forces a fresh state on navigation so zoom resets
            key={current.id}
            src={url}
            alt={current.name}
            // Swipe navigation only fires when the user is fully zoomed
            // out — at zoom > 1 the same drag pans the image instead.
            onSwipeLeft={hasNext ? goNext : null}
            onSwipeRight={hasPrev ? goPrev : null}
          />
        )}
        {isVideo && (
          // `key` forces React to remount the video element on navigation,
          // ensuring the previous file stops playing before the next starts.
          <video
            key={current.id}
            src={url}
            controls
            autoPlay
            playsInline
            className={styles.video}
          />
        )}
        {!isImage && !isVideo && (
          <div className={styles.docFallback}>
            <FileIcon />
            <span className={styles.docName}>{current.name}</span>
            <a href={url} target="_blank" rel="noopener noreferrer" className={styles.docOpen}>
              Open in new tab
            </a>
          </div>
        )}
      </div>

      {/* Caption strip */}
      <div className={styles.caption} onClick={(e) => e.stopPropagation()}>
        <div className={styles.capName}>{current.name}</div>
        <div className={styles.capMeta}>
          {current.uploaded_by ? `${current.uploaded_by} · ` : ''}
          {current.created_at ? format(new Date(current.created_at), 'MMM d, yyyy h:mm a') : ''}
          {list.length > 1 && (
            <span className={styles.capIndex}> · {idx + 1} / {list.length}</span>
          )}
        </div>
        <a
          href={dlUrl}
          download={current.name}
          className={styles.dlBtn}
          onClick={(e) => e.stopPropagation()}
        >
          <DownloadIcon /> Download
        </a>
      </div>
    </div>
  );
}

// ── ZoomableImage ──────────────────────────────────────────────────────────
// Image with mouse-wheel + pinch zoom and drag-to-pan when zoomed in. Pretty
// close to the math the plan viewer uses, but framed inside a fixed-aspect
// container so the image always stays visually centered when at zoom = 1.
//
// Coordinate space: (x, y) is the position of the image's top-left corner
// inside the wrapper. At zoom = 1 the image is "fit" inside the wrapper
// (CSS object-fit-style sizing), so we keep pan = (0, 0) as the anchor and
// scale relative to that. At higher zooms the user can drag the image to
// see hidden parts; we don't currently bound the pan (no edge clamping)
// because the lightbox backdrop absorbs anything that escapes — keeping the
// math simple and the gesture forgiving.

const MIN_ZOOM = 1.0;
const MAX_ZOOM = 8.0;
const DBL_TAP_MS = 280;
// Horizontal-swipe threshold for nav. ~80px feels right on iPad — small
// enough to be a fast flick, big enough to avoid accidental nav from a
// bumpy single tap.
const SWIPE_THRESHOLD_PX = 80;
// Don't fire swipe nav if the user moved more vertically than horizontally
// — it was probably an aborted-pan or a noisy tap.
const SWIPE_HV_RATIO     = 1.5;

function ZoomableImage({ src, alt, onSwipeLeft, onSwipeRight }) {
  const wrapRef = React.useRef(null);
  const [zoom, setZoom] = React.useState(1);
  const [pan,  setPan]  = React.useState({ x: 0, y: 0 });
  // Visual offset applied during a swipe-at-zoom=1 so the image moves with
  // the finger. Reset to 0 on release (committed swipe → unmounted via key
  // change in parent; non-committed → animated back to 0).
  const [swipeX, setSwipeX]             = React.useState(0);
  const [swipeAnimating, setAnimating]  = React.useState(false);

  // Refs hold the latest state for handlers attached imperatively (wheel) +
  // for cross-event tracking (pinch/pan). React state still drives renders.
  const zoomRef = React.useRef(1);
  const panRef  = React.useRef({ x: 0, y: 0 });
  React.useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  React.useEffect(() => { panRef.current  = pan;  }, [pan]);

  const pointers   = React.useRef(new Map()); // pointerId -> { x, y }
  const pinchStart = React.useRef(null);      // { dist, midX, midY, zoom, pan }
  const panStart   = React.useRef(null);      // { x, y, zoom, pan }
  const swipeStart = React.useRef(null);      // { x, y } — only when zoom=1
  const lastTap    = React.useRef(0);

  function applyZoomAt(newZoom, anchorX, anchorY) {
    const z = zoomRef.current;
    const p = panRef.current;
    const factor = newZoom / z;
    // Anchor the cursor / midpoint in image space — same trick as the plan
    // viewer's wheel handler.
    const newPan = {
      x: anchorX - (anchorX - p.x) * factor,
      y: anchorY - (anchorY - p.y) * factor,
    };
    if (newZoom <= 1.001) {
      // Snap back to centered when the user zooms all the way out.
      setZoom(1); setPan({ x: 0, y: 0 });
    } else {
      setZoom(newZoom); setPan(newPan);
    }
  }

  // Wheel zoom — passive: false because we preventDefault to suppress the
  // surrounding page scroll on mouse-wheel use.
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    function onWheel(e) {
      e.preventDefault();
      let dy = e.deltaY;
      if (e.deltaMode === 1) dy *= 40;       // line → px
      else if (e.deltaMode === 2) dy *= 400; // page → px
      const factor = Math.exp(-dy * 0.0015);
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoomRef.current * factor));
      const rect = el.getBoundingClientRect();
      applyZoomAt(next, e.clientX - rect.left, e.clientY - rect.top);
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  function onPointerDown(e) {
    const el = wrapRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      // Begin pinch — record start dist + midpoint + current state. Cancel
      // any in-progress swipe + snap-back animation.
      const pts = Array.from(pointers.current.values());
      const dx  = pts[1].x - pts[0].x;
      const dy  = pts[1].y - pts[0].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const rect = el.getBoundingClientRect();
      pinchStart.current = {
        dist,
        midX: (pts[0].x + pts[1].x) / 2 - rect.left,
        midY: (pts[0].y + pts[1].y) / 2 - rect.top,
        zoom: zoomRef.current,
        pan:  { ...panRef.current },
      };
      panStart.current = null;
      swipeStart.current = null;
      setSwipeX(0);
      setAnimating(false);
      e.preventDefault();
      return;
    }

    // Single pointer — split on zoom level. Zoomed in → drag pans the
    // image. Zoomed out (1x) → drag is a horizontal swipe that navigates
    // the carousel.
    if (zoomRef.current > 1.001) {
      panStart.current = {
        x: e.clientX, y: e.clientY,
        zoom: zoomRef.current,
        pan:  { ...panRef.current },
      };
      e.preventDefault();
    } else {
      swipeStart.current = { x: e.clientX, y: e.clientY, t: Date.now() };
      // If the user starts swiping mid-snap-back animation, kill the
      // transition so the new gesture starts clean.
      setAnimating(false);
    }

    // Double-tap toggles between 1x and 2.5x at the tap location.
    const now = Date.now();
    if (now - lastTap.current < DBL_TAP_MS && pointers.current.size === 1) {
      const rect = el.getBoundingClientRect();
      const ax = e.clientX - rect.left;
      const ay = e.clientY - rect.top;
      const next = zoomRef.current > 1.01 ? 1 : 2.5;
      applyZoomAt(next, ax, ay);
      // Cancel the swipe gesture too — the user double-tapped, not swiped.
      swipeStart.current = null;
      setSwipeX(0);
      lastTap.current = 0;
      e.preventDefault();
    } else {
      lastTap.current = now;
    }
  }

  function onPointerMove(e) {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pinchStart.current && pointers.current.size >= 2) {
      const pts = Array.from(pointers.current.values()).slice(0, 2);
      const dx = pts[1].x - pts[0].x;
      const dy = pts[1].y - pts[0].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const factor = dist / pinchStart.current.dist;
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, pinchStart.current.zoom * factor));
      const rect = wrapRef.current.getBoundingClientRect();
      const curMidX = (pts[0].x + pts[1].x) / 2 - rect.left;
      const curMidY = (pts[0].y + pts[1].y) / 2 - rect.top;
      const startMidX = pinchStart.current.midX;
      const startMidY = pinchStart.current.midY;
      const f = newZoom / pinchStart.current.zoom;
      // Same composed pan + zoom formula the plan viewer uses — current
      // midpoint stays anchored to the image point that was under the
      // starting midpoint, with extra translation if the midpoint moved.
      setZoom(newZoom);
      setPan({
        x: curMidX - (startMidX - pinchStart.current.pan.x) * f,
        y: curMidY - (startMidY - pinchStart.current.pan.y) * f,
      });
      return;
    }

    if (panStart.current) {
      setPan({
        x: panStart.current.pan.x + (e.clientX - panStart.current.x),
        y: panStart.current.pan.y + (e.clientY - panStart.current.y),
      });
      return;
    }

    // Swipe gesture (only at zoom = 1). Track horizontal delta so the image
    // moves with the finger, giving the swipe some heft. Vertical delta is
    // ignored visually but considered at release time.
    if (swipeStart.current) {
      const dx = e.clientX - swipeStart.current.x;
      setSwipeX(dx);
    }
  }

  function onPointerUp(e) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) panStart.current = null;

    // Resolve a swipe-at-zoom=1 gesture if one was active. Only when this
    // was the last finger/pointer up — otherwise we'd nav mid-pinch.
    if (pointers.current.size === 0 && swipeStart.current) {
      const dx = e.clientX - swipeStart.current.x;
      const dy = e.clientY - swipeStart.current.y;
      swipeStart.current = null;

      const horizontal = Math.abs(dx) > Math.abs(dy) * SWIPE_HV_RATIO;
      const farEnough  = Math.abs(dx) > SWIPE_THRESHOLD_PX;

      if (horizontal && farEnough) {
        // Commit the swipe. dx > 0 → finger went right → previous photo.
        // The parent will replace this component (key changes), so we
        // don't need to reset swipeX locally.
        if (dx > 0 && onSwipeRight) onSwipeRight();
        else if (dx < 0 && onSwipeLeft) onSwipeLeft();
        else {
          // No callback for that direction (already at the end) — snap back.
          setAnimating(true);
          setSwipeX(0);
          setTimeout(() => setAnimating(false), 220);
        }
      } else {
        // Didn't cross threshold or wasn't horizontal — snap back to 0.
        setAnimating(true);
        setSwipeX(0);
        setTimeout(() => setAnimating(false), 220);
      }
    }
  }

  return (
    <div
      ref={wrapRef}
      className={styles.zoomWrap}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ touchAction: 'none', cursor: zoom > 1.001 ? 'grab' : 'zoom-in' }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className={`${styles.image} ${swipeAnimating ? styles.swipeAnimate : ''}`}
        style={{
          // swipeX only applies at zoom=1 (it's reset to 0 elsewhere). It
          // composes outside the scale so it doesn't multiply with zoom.
          transform: `translate(${pan.x + swipeX}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
        }}
      />
    </div>
  );
}

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);
const ChevronLeft = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ChevronRight = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const DownloadIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M8 1v9M5 7l3 3 3-3M2 12v2a1 1 0 001 1h10a1 1 0 001-1v-2"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const FileIcon = () => (
  <svg width="56" height="56" viewBox="0 0 20 20" fill="none">
    <path d="M5 2h7l4 4v11a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z"
      stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M12 2v4h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
